const STORAGE_KEY = 'user_affinity_v1';
//! Make sure to set this to `false` in production builds.
const DEBUG = false;

const DECAY_RATE = 0.9; //0.95 will result in high scores very fast. 0.85 will be very competitive. trying in between

const BOUNCE_INTERACTION_CUTOFF_TIME = 5; // in seconds
const HIGH_INTERACTION_CUTOFF_TIME = 30; // in seconds
const COLD_START_THRESHOLD = 3;

type InteractionType = 'click' | 'swipe' | 'time_spent';

interface AffinityData {
  lastUpdated: number; // Timestamp (ms)
  totalInteractions: number; // To track "maturity" of profile
  scores: {
    [interestId: string]: number; // Float (0.0 to infinity)
  };
}

// Weight Configuration
const WEIGHTS: Record<InteractionType, number> = {
  click: 1.0,
  swipe: 0.05,
  time_spent: 0.5, // Handled dynamically
};

const TIME_WEIGHT_MULTIPLIER_HIGH_ATTENTION = 2.0
const TIME_WEIGHT_MULTIPLIER_LOW_ATTENTION = 1.0

export const trackInterest = (
  interestIds: string[],
  type: InteractionType,
  durationSeconds: number = 0
) => {
  // Disable in SSR.
  if (import.meta.env.SSR) return;

  if (DEBUG) {
    console.log(
      `[AffinityScorer] Tracking interest: ${interestIds.join(
        ', '
      )} | type: ${type} | duration: ${durationSeconds}s`
    );
  }

  // 1. Load Data
  const raw = localStorage.getItem(STORAGE_KEY);
  const data: AffinityData = raw
    ? JSON.parse(raw)
    : { lastUpdated: Date.now(), totalInteractions: 0, scores: {} };

  if (DEBUG) console.log('[AffinityScorer] Before update:', JSON.parse(JSON.stringify(data)));

  // 2. Calculate Time Factor
  let timeMultiplier = 1;
  if (type === 'time_spent') {
    if (durationSeconds < BOUNCE_INTERACTION_CUTOFF_TIME) {
      if (DEBUG) console.log(`[AffinityScorer] Bounce detected (${durationSeconds}s). Ignoring.`);
      return; // Bounce: ignore
    }
    timeMultiplier =
      durationSeconds > HIGH_INTERACTION_CUTOFF_TIME
        ? TIME_WEIGHT_MULTIPLIER_HIGH_ATTENTION // High focus time in article: boost
        : TIME_WEIGHT_MULTIPLIER_LOW_ATTENTION;
    if (DEBUG) console.log(`[AffinityScorer] Time multiplier: ${timeMultiplier}`);
  }

  // 3. Apply Decay to ALL existing scores (Aging process)
  // Only decay on major actions (like opening a new lookbook), not every swipe
  if (type === 'click') {
    if (DEBUG) console.log(`[AffinityScorer] Applying decay (${DECAY_RATE}) to all scores.`);
    for (const key in data.scores) {
      data.scores[key] *= DECAY_RATE;
      // --- Garbage Collection ---
      // If a score is effectively zero, delete it to keep the object small and fast.
      if (data.scores[key] < 0.1) {
        if (DEBUG)
          console.log(`[AffinityScorer] Pruning stale interest: ${key}`);
        delete data.scores[key];
      }
    }
  }

  // 4. Boost the active interests
  const boost = (WEIGHTS[type] || 1) * timeMultiplier;
  if (DEBUG) console.log(`[AffinityScorer] Calculated boost: ${boost}`);

  interestIds.forEach((id) => {
    const current = data.scores[id] || 0;
    data.scores[id] = current + boost;
    if (DEBUG) console.log(`[AffinityScorer] Updated score for '${id}': ${current} -> ${data.scores[id]}`);
  });

  // 5. Update Metadata & Save
  data.totalInteractions += 1;
  data.lastUpdated = Date.now();
  localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  if (DEBUG) console.log('[AffinityScorer] After update:', data);
};

export const getTopInterests = (limit: number = 3): string[] => {
  if (typeof window === 'undefined') return [];
  const raw = localStorage.getItem(STORAGE_KEY);
  if (!raw) {
    if (DEBUG) console.log('[AffinityScorer] No affinity data found in localStorage.');
    return [];
  }

  const data: AffinityData = JSON.parse(raw);
  if (DEBUG) console.log('[AffinityScorer] Getting top interests from:', data);

  // Cold Start Check
  if (data.totalInteractions < COLD_START_THRESHOLD) {
    if (DEBUG) console.log(`[AffinityScorer] Cold start: only ${data.totalInteractions} interactions. Need ${COLD_START_THRESHOLD} to start personalizing.`);
    return [];
  }

  return (
    Object.entries(data.scores)
      .sort(([, a], [, b]) => b - a)
      .slice(0, limit)
      .map(([id]) => id)
  );
};

export const getCurrentNumberOfInteractions = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const data: AffinityData = raw
      ? JSON.parse(raw)
      : { lastUpdated: Date.now(), totalInteractions: 0, scores: {} };
    if (data && data.totalInteractions) {
      return data.totalInteractions;
    }
    return 0;
  } catch (_) {
    return 0;
  }

}

export const debugScorer = () => {
  const raw = localStorage.getItem(STORAGE_KEY);
  if (!raw) {
    console.log('No affinity data found.');
    return;
  }
  const data: AffinityData = JSON.parse(raw);

  const mapping: Record<string, string> = {};
  try {
    const queries = (window as any).reactQueryData?.queries;
    if (Array.isArray(queries)) {
      const q = queries.find((query: any) => {
        return (
          Array.isArray(query.queryKey) &&
          query.queryKey.length >= 2 &&
          query.queryKey[1] === 'interests'
        );
      });
      if (q?.state?.data?.interests) {
        q.state.data.interests.forEach(
          (obj) => {
            const k = obj.uid
            const n = obj.name.en;
            mapping[k] = n
          }
        );
      }
    }
  } catch (e) {
    console.error('Error reading reactQueryData', e);
  }

  const scores = Object.entries(data.scores).sort((a, b) => b[1] - a[1]);
  if (scores.length === 0) {
    console.log('No scores to display.');
    return;
  }

  const maxScore = scores[0][1];

  console.log(`User Affinity (Total Interactions: ${data.totalInteractions})`);
  scores.forEach(([id, score]) => {
    const name = mapping[id] || id;
    const barLength = Math.floor((score / maxScore) * 40);
    const bar = '='.repeat(barLength) + '>';
    console.log(`${name.padEnd(30)} ${score.toFixed(2)} ${bar}`);
  });
};

if (typeof window !== 'undefined') {
  (window as any).debugScorer = debugScorer;
}
